import { Button } from "@/components/ui/baseUi/button"
import { getFirstErrorMessage } from "@/components/ui/forms/helpers/formErrorHelpers"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/baseUi/select"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { word } from "@modules/core/utils/i18n"
import { getCurrencySymbol } from "@modules/currency/currency"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { BoxOfferListSelection } from "@modules/promoCode/components/boxOfferListSelection"
import { ProgramBundleListSelection } from "@modules/promoCode/components/programBundleListSelection"
import { ProgramListSelection } from "@modules/promoCode/components/programListSelection"
import { BoxPromoCodeEntity } from "@modules/promoCode/entities/boxPromoCodeEntity"
import { PromoCodeEntity } from "@modules/promoCode/entities/promoCodeEntity"
import { usePromoCodeForm } from "@modules/promoCode/hooks/usePromoCodeForm"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { displayedFullDateFormat } from "@modules/utils/dateUtils"
import { capitalize } from "@modules/utils/stringUtils"
import dayjs from "dayjs"
import { CalendarIcon, LoaderCircle } from "lucide-react"
import { useImperativeHandle, useRef } from "react"
import { Calendar } from "@/components/ui/calendar"

export type PromoCodeFormViewRef = {
  onClose: () => Promise<boolean>
}

type PromoCodeFormViewProps = {
  updatedPromoCode?: PromoCodeEntity | BoxPromoCodeEntity
  className?: string
  ref?: React.Ref<PromoCodeFormViewRef>
} & Omit<React.ComponentProps<"form">, "ref">

export function PromoCodeFormView({ updatedPromoCode, className, ref, ...props }: PromoCodeFormViewProps) {
  const language = useLanguage()
  const { result: box } = useCurrentBox()
  const containerRef = useRef<HTMLFormElement>(null)
  const confirmPopup = useConfirmPopup()

  const {
    form,
    promoCodeTypesOptions,
    expirationOptions,
    applicationDurationOptions,
    maxAttendeesOptions,
    onSelectProgram,
    onSelectAllPrograms,
    onSelectProgramBundle,
    onSelectAllProgramBundles,
    onSelectBoxOffer,
    onSelectAllBoxOffers,
    schema,
  } = usePromoCodeForm(updatedPromoCode)

  useImperativeHandle(ref, () => ({
    onClose: async () => {
      if (form.state.isPristine) return true

      return await new Promise<boolean>((resolve) => {
        confirmPopup.show({
          title: updatedPromoCode
            ? word("promoCode.form.closeMessage.editionTitle")
            : word("promoCode.form.closeMessage.creationTitle"),
          message: word("promoCode.form.closeMessage.message"),
          accept: () => {
            resolve(true)
          },
          cancel: () => {
            resolve(false)
          },
          footerProps: {
            confirmText: word("global.ok"),
          },
        })
      })
    },
  }))

  const requiredNumber = ({ value }: { value: number | null | undefined }) =>
    value === null || value === undefined ? word("global.form.required") : undefined

  const scrollToFirstError = () => {
    const errorElements = containerRef.current?.querySelectorAll('[aria-invalid="true"]')
    if (errorElements && errorElements.length > 0) {
      document
        .querySelector('[data-slot="right-sidebar-content"]')
        ?.scrollTo({ top: (errorElements[0] as HTMLElement).offsetTop - 30, behavior: "smooth" })
    }
  }

  const handleClickSubmit = async () => {
    await form.validateAllFields("submit")
    await form.validate("submit")

    if (!form.state.isValid) {
      requestAnimationFrame(scrollToFirstError)
      return
    }

    await form.handleSubmit()
  }

  return (
    <form
      className={cn("flex flex-col px-5 pt-18 pb-8", className)}
      {...props}
      ref={containerRef}
      onSubmit={(e) => {
        e.preventDefault()
        void handleClickSubmit()
      }}
    >
      {/* TITLE AND DESCRIPTION */}
      <Text fontStyle="TL">{word("promoCode.form.title")}</Text>
      <Text fontStyle="BM" className="mt-4 mb-6">
        {word("promoCode.form.description")}
      </Text>

      {/* INPUT FIELDS */}
      <div className="flex flex-col gap-4">
        {/* Title */}
        <form.AppField name="title" validators={{ onChange: schema.shape.title, onBlur: schema.shape.title }}>
          {(field) => (
            <field.Field>
              <field.Label required>{word("promoCode.form.input.title.label")} </field.Label>
              <field.Content>
                <field.Input type="text" placeholder={word("promoCode.form.input.title.placeholder")} maxLength={40} />
                <field.Message />
              </field.Content>
            </field.Field>
          )}
        </form.AppField>

        {/* Code */}
        <form.AppField name="code" validators={{ onChange: schema.shape.code, onBlur: schema.shape.code }}>
          {(field) => (
            <field.Field>
              <field.Label required>{word("promoCode.form.input.code.label")} </field.Label>
              <field.Content>
                <field.Input
                  type="text"
                  placeholder={word("promoCode.form.input.code.placeholder")}
                  maxLength={30}
                  onChange={(e) => {
                    field.handleChange(e.target.value.toUpperCase())
                  }}
                />
                <Text fontStyle="BS" className="px-2 whitespace-pre-line text-textDefault-caption">
                  {word("promoCode.form.input.code.description")}
                </Text>
                <field.Message />
              </field.Content>
            </field.Field>
          )}
        </form.AppField>

        {/* Start date */}
        <form.AppField name="startAt" validators={{ onChange: schema.shape.startAt, onBlur: schema.shape.startAt }}>
          {(field) => (
            <field.Field>
              <field.Label required>{word("promoCode.form.input.startAt.label")}</field.Label>
              <field.Content>
                <Popover>
                  <PopoverTrigger
                    render={
                      <Button
                        variant="outline"
                        className={cn(
                          "flex h-11 w-full text-left font-normal whitespace-normal hover:border hover:border-borderPrimary",
                        )}
                      >
                        {field.state.value && !dayjs(field.state.value).isSame(dayjs(), "day") ? (
                          capitalize(dayjs(field.state.value).format(displayedFullDateFormat))
                        ) : (
                          <span>{word("promoCode.form.input.startAt.placeholder")}</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    }
                  />
                  <PopoverContent className="w-auto border-0 p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.state.value}
                      disabled={(date) =>
                        date > dayjs().add(1, "year").toDate() || date < dayjs().startOf("day").toDate()
                      }
                      onSelect={(date) => {
                        if (date) field.handleChange(date)
                      }}
                    />
                  </PopoverContent>
                </Popover>
                <Text fontStyle="BS" className="px-2 whitespace-pre-line text-textDefault-caption">
                  {word("promoCode.form.input.startAt.description")}
                </Text>
                <field.Message />
              </field.Content>
            </field.Field>
          )}
        </form.AppField>

        {/* Type + value */}
        <div className="flex flex-col gap-2">
          <form.AppField name="type" validators={{ onChange: schema.shape.type, onBlur: schema.shape.type }}>
            {(field) => (
              <field.Field>
                <field.Label required>{word("promoCode.form.input.type.label")} </field.Label>
                <field.Content>
                  <Select
                    items={promoCodeTypesOptions}
                    defaultValue={field.state.value}
                    onValueChange={(value) => {
                      if (!value) return
                      field.handleChange(value)
                    }}
                  >
                    <SelectTrigger id="durationType">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {promoCodeTypesOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <form.AppField name="value" validators={{ onChange: requiredNumber, onBlur: requiredNumber }}>
            {(field) => (
              <field.Field>
                <field.Content className="flex flex-row items-center">
                  <form.Subscribe selector={(state) => state.values.type}>
                    {(type) => (
                      <>
                        <field.NumberInput
                          max={type === CouponType.PERCENT ? 100 : undefined}
                          min={0}
                          data-testid="value"
                        />
                        <Text fontStyle="TM" className="ml-1">
                          {type === CouponType.DIRECT ? getCurrencySymbol(form.state.values.currency, language) : " %"}
                        </Text>
                      </>
                    )}
                  </form.Subscribe>
                </field.Content>
                <field.Message />
              </field.Field>
            )}
          </form.AppField>

          <Text fontStyle="BS" className="mb-4 whitespace-pre-line text-textDefault-caption">
            {word("promoCode.form.input.type.description")}
          </Text>
        </div>

        {/* Expiration date */}
        <div className="flex flex-col gap-2">
          <form.AppField
            name="hasExpirationDate"
            validators={{ onChange: schema.shape.hasExpirationDate, onBlur: schema.shape.hasExpirationDate }}
          >
            {(field) => (
              <field.Field>
                <field.Label required>{word("promoCode.form.input.expiration.label")} </field.Label>
                <field.Content>
                  <Select
                    items={expirationOptions}
                    defaultValue={field.state.value ? "hasExpirationDate" : "donthaveExpirationDate"}
                    onValueChange={(value) => {
                      if (value === "hasExpirationDate") {
                        form.setFieldValue("expirationDate", dayjs().add(1, "day").toDate())
                        field.handleChange(true)
                      } else {
                        form.setFieldValue("expirationDate", undefined)
                        field.handleChange(false)
                      }
                    }}
                  >
                    <SelectTrigger id="hasExpirationDate">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {expirationOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <form.Subscribe selector={(s) => s.values.hasExpirationDate}>
            {(hasExpirationDate) =>
              hasExpirationDate ? (
                <form.AppField
                  name="expirationDate"
                  validators={{ onChange: schema.shape.expirationDate, onBlur: schema.shape.expirationDate }}
                >
                  {(field) => (
                    <field.Field>
                      <field.Content>
                        <Popover>
                          <PopoverTrigger
                            render={
                              <Button
                                variant="outline"
                                className={cn(
                                  "flex h-11 w-full text-left font-normal whitespace-normal hover:border hover:border-borderPrimary",
                                  !field.state.value && "text-textDefault-caption",
                                )}
                              >
                                {field.state.value ? (
                                  capitalize(dayjs(field.state.value).format(displayedFullDateFormat))
                                ) : (
                                  <span>{word("global.pickADate")}</span>
                                )}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            }
                          />
                          <PopoverContent className="w-auto border-0 p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={field.state.value}
                              disabled={(date) => date > dayjs().add(1, "year").toDate() || date < dayjs().toDate()}
                              onSelect={(date) => {
                                if (date) field.handleChange(date)
                              }}
                            />
                          </PopoverContent>
                        </Popover>
                        <field.Message />
                      </field.Content>
                    </field.Field>
                  )}
                </form.AppField>
              ) : null
            }
          </form.Subscribe>

          <Text fontStyle="BS" className="mb-4 whitespace-pre-line text-textDefault-caption">
            {word("promoCode.form.input.expirationDate.description")}
          </Text>
        </div>

        {/* Duration limit */}
        <div className="flex flex-col gap-2">
          <form.AppField
            name="hasDurationLimit"
            validators={{ onChange: schema.shape.hasDurationLimit, onBlur: schema.shape.hasDurationLimit }}
          >
            {(field) => (
              <field.Field>
                <field.Label required>{word("promoCode.form.input.applicationDuration.label")} </field.Label>
                <field.Content>
                  <Select
                    items={applicationDurationOptions}
                    defaultValue={field.state.value ? "hasDurationLimit" : "donthaveDurationLimit"}
                    onValueChange={(value) => {
                      if (value === "hasDurationLimit") {
                        form.setFieldValue("durationInMonths", undefined)
                        field.handleChange(true)
                      } else {
                        form.setFieldValue("durationInMonths", 1)
                        field.handleChange(false)
                      }
                    }}
                  >
                    <SelectTrigger id="hasDurationLimit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {applicationDurationOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <form.Subscribe selector={(s) => s.values.hasDurationLimit}>
            {(hasDurationLimit) =>
              hasDurationLimit ? (
                <form.AppField
                  name="durationInMonths"
                  validators={{ onChange: requiredNumber, onBlur: requiredNumber }}
                >
                  {(field) => (
                    <field.Field>
                      <field.Content className="flex flex-row items-center">
                        <field.NumberInput max={120} step={1} />
                        <form.Subscribe selector={(s) => s.values.durationInMonths}>
                          {(durationInMonths) => (
                            <Text fontStyle="TM" className="ml-1">
                              {word("global.month.label", { count: durationInMonths ?? 0 })}
                            </Text>
                          )}
                        </form.Subscribe>
                      </field.Content>
                      <field.Message />
                    </field.Field>
                  )}
                </form.AppField>
              ) : null
            }
          </form.Subscribe>

          <Text fontStyle="BS" className="mb-4 whitespace-pre-line text-textDefault-caption">
            {word("promoCode.form.input.applicationDuration.description")}
          </Text>
        </div>

        {/* Max attendees */}
        <div className="flex flex-col gap-2">
          <form.AppField
            name="maxAttendeesLimited"
            validators={{ onChange: schema.shape.maxAttendeesLimited, onBlur: schema.shape.maxAttendeesLimited }}
          >
            {(field) => (
              <field.Field>
                <field.Label required>{word("promoCode.form.input.maxAttendees.label")} </field.Label>
                <field.Content>
                  <Select
                    items={maxAttendeesOptions}
                    defaultValue={field.state.value ? "maxAttendeesLimited" : "donthaveMaxAttendeesLimited"}
                    onValueChange={(value) => {
                      if (value === "maxAttendeesLimited") {
                        form.setFieldValue("maxAttendees", undefined)
                        field.handleChange(true)
                      } else {
                        form.setFieldValue("maxAttendees", undefined)
                        field.handleChange(false)
                      }
                    }}
                  >
                    <SelectTrigger id="maxAttendeesLimited">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {maxAttendeesOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <form.Subscribe selector={(s) => s.values.maxAttendeesLimited}>
            {(maxAttendeesLimited) =>
              maxAttendeesLimited ? (
                <form.AppField name="maxAttendees" validators={{ onChange: requiredNumber, onBlur: requiredNumber }}>
                  {(field) => (
                    <field.Field>
                      <field.Content className="flex flex-row items-center">
                        <field.NumberInput max={1e5} step={1} />
                        <form.Subscribe selector={(s) => s.values.maxAttendees}>
                          {(maxAttendees) => (
                            <Text fontStyle="TM" className="ml-1">
                              {word("global.customer.label", { count: maxAttendees ?? 0 })}
                            </Text>
                          )}
                        </form.Subscribe>
                      </field.Content>
                      <field.Message />
                    </field.Field>
                  )}
                </form.AppField>
              ) : null
            }
          </form.Subscribe>

          <Text fontStyle="BS" className="mb-4 whitespace-pre-line text-textDefault-caption">
            {word("promoCode.form.input.maxAttendees.description")}
          </Text>
        </div>

        {/* Only new customer */}
        <div className="flex flex-col gap-2">
          <form.AppField
            name="onlyNewCustomer"
            validators={{ onChange: schema.shape.onlyNewCustomer, onBlur: schema.shape.onlyNewCustomer }}
          >
            {(field) => (
              <field.Field>
                <field.Label required>{word("promoCode.form.input.filter.label")}</field.Label>
                <field.Content>
                  <field.SwitchInline textSwitch={word("promoCode.form.input.onlyNewCustomer.label")} />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>
          <Text fontStyle="BS" className="mb-4 whitespace-pre-line text-textDefault-caption">
            {word("promoCode.form.input.onlyNewCustomer.description")}
          </Text>
        </div>

        {/* Related programs / offers label */}
        <Text className="text-textDefault-body" fontStyle="LS" style={{ lineHeight: "21px", marginBottom: 7 }}>
          {(box
            ? word("promoCode.form.input.relatedOffers")
            : word("promoCode.form.input.relatedPrograms")
          ).toUpperCase()}
          <Text fontStyle="LL" className="text-red-500">
            {" *"}
          </Text>
        </Text>
      </div>

      {/* Selection lists */}
      {box ? (
        <form.AppField
          name="boxOffers"
          validators={{ onChange: schema.shape.boxOffers, onBlur: schema.shape.boxOffers }}
        >
          {(field) => (
            <BoxOfferListSelection
              onSelectBoxOffer={onSelectBoxOffer}
              onSelectAllBoxOffers={onSelectAllBoxOffers}
              selectedBoxOfferIds={field.state.value}
              promoCodeType={form.state.values.type}
              promoCodeValue={form.state.values.value ?? undefined}
              promoCodeDuration={form.state.values.durationInMonths ?? undefined}
              error={
                field.state.meta.isTouched && field.state.meta.errors.length > 0 ? (
                  <p className="label-small text-textError">{getFirstErrorMessage(field.state.meta.errors)}</p>
                ) : undefined
              }
            />
          )}
        </form.AppField>
      ) : (
        <>
          <form.AppField
            name="programs"
            validators={{ onChange: schema.shape.programs, onBlur: schema.shape.programs }}
          >
            {(field) => (
              <ProgramListSelection
                onSelectProgram={onSelectProgram}
                onSelectAllPrograms={onSelectAllPrograms}
                selectedProgramIds={field.state.value}
                promoCodeType={form.state.values.type}
                promoCodeValue={form.state.values.value ?? undefined}
                promoCodeDuration={form.state.values.durationInMonths ?? undefined}
                error={
                  field.state.meta.isTouched && field.state.meta.errors.length > 0 ? (
                    <p className="label-small text-textError">{getFirstErrorMessage(field.state.meta.errors)}</p>
                  ) : undefined
                }
              />
            )}
          </form.AppField>

          <form.AppField
            name="programBundles"
            validators={{ onChange: schema.shape.programBundles, onBlur: schema.shape.programBundles }}
          >
            {(field) => (
              <ProgramBundleListSelection
                onSelectProgram={onSelectProgramBundle}
                onSelectAllPrograms={onSelectAllProgramBundles}
                selectedProgramIds={field.state.value}
                promoCodeType={form.state.values.type}
                promoCodeValue={form.state.values.value ?? undefined}
                promoCodeDuration={form.state.values.durationInMonths ?? undefined}
                error={
                  field.state.meta.isTouched && field.state.meta.errors.length > 0 ? (
                    <p className="label-small text-textError">{getFirstErrorMessage(field.state.meta.errors)}</p>
                  ) : undefined
                }
              />
            )}
          </form.AppField>
        </>
      )}

      <form.Subscribe selector={(s) => s.isSubmitting}>
        {(isSubmitting) => (
          <Button
            variant="primary"
            size="lg"
            type="submit"
            className="mt-5 w-full"
            disabled={isSubmitting}
            data-testid="promocode-form-validate"
            isLoading={isSubmitting}
          >
            {isSubmitting ? <LoaderCircle /> : null}
            {updatedPromoCode ? word("promoCode.form.submit.update") : word("promoCode.form.submit.create")}
          </Button>
        )}
      </form.Subscribe>
    </form>
  )
}
